import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { FormError } from '@/components/ui/FormLayout'
import { FormSection } from '@/components/ui/FormSection'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { TextField } from '@/components/ui/TextField'
import { UserPlusIcon } from '@/components/ui/icons'
import { useAuth } from '@/features/auth/authContext'
import { useSubmitLeadIntake } from '@/features/pipeline/hooks/useSubmitLeadIntake'
import {
  type LeadIntakeInput,
  leadIntakeSchema,
} from '@/features/pipeline/schemas/leadIntakeSchema'
import { ApiError } from '@/api/errors'
import { useTerm } from '@/hooks/useTerm'

import form from '@/components/ui/FormLayout/formLayout.module.css'
import styles from './LeadIntakeModal.module.css'

const FORM_ID = 'lead-intake-form'

const FORM_FIELD_BY_REQUEST_FIELD: Record<string, keyof LeadIntakeInput> = {
  firstName: 'name',
  lastName: 'name',
  email: 'email',
  phone: 'phone',
  origin: 'origin',
}

const EMPTY_VALUES: LeadIntakeInput = {
  name: '',
  email: '',
  phone: '',
  studio: '',
  notes: '',
  consent: false,
  origin: '',
}

interface LeadIntakeModalProps {
  open: boolean
  onClose: () => void
}

/** Lets front-desk staff log a request the way it would arrive from the studio's own booking form. */
export function LeadIntakeModal({ open, onClose }: LeadIntakeModalProps) {
  const submitLead = useSubmitLeadIntake()
  const customer = useTerm('customer')
  // The lead sources the server enumerates, straight off the sign-in response.
  const { origins, locations, location } = useAuth()
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LeadIntakeInput>({
    resolver: zodResolver(leadIntakeSchema),
    defaultValues: {
      ...EMPTY_VALUES,
      origin: origins[0]?.origin ?? '',
      studio: location?.publicId ?? '',
    },
  })

  useEffect(() => {
    if (open)
      reset({ ...EMPTY_VALUES, origin: origins[0]?.origin ?? '', studio: location?.publicId ?? '' })
  }, [open, origins, location?.publicId, reset])

  const onSubmit = handleSubmit(async (values) => {
    if (!locations.some((item) => item.publicId === values.studio)) {
      setError('studio', { message: 'Bitte wählen Sie einen verfügbaren Standort aus.' })
      return
    }
    if (values.studio !== location?.publicId) {
      setError('studio', {
        message:
          'Bitte wechseln Sie oben zuerst zu diesem Standort. Die Kundenerstellung gehört zum aktiven Standort.',
      })
      return
    }
    try {
      await submitLead.mutateAsync(values)
      onClose()
    } catch (error) {
      const apiError = ApiError.from(error)

      for (const field of Object.keys(apiError.fieldErrors)) {
        const formField = FORM_FIELD_BY_REQUEST_FIELD[field]
        if (formField) setError(formField, { message: 'Bitte prüfen Sie dieses Feld.' })
      }

      setError('root', { message: apiError.message })
    }
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${customer.singular} erstellen`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            isLoading={isSubmitting}
            leadingIcon={<UserPlusIcon />}
          >
            {`${customer.singular} erstellen`}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} noValidate>
        <FormError message={errors.root?.message} />

        <FormSection title="Kontaktdaten">
          <div className={form.grid}>
            <TextField
              className={form.full}
              label="Vorname/Nachname"
              placeholder="Vor- und Nachname"
              autoComplete="name"
              error={errors.name?.message}
              {...register('name')}
            />

            <TextField
              label="E-Mail-Adresse"
              type="email"
              placeholder="name@example.com"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              error={errors.email?.message}
              {...register('email')}
            />

            <TextField
              label="Telefon"
              type="tel"
              placeholder="+49 …"
              autoComplete="tel"
              inputMode="tel"
              error={errors.phone?.message}
              {...register('phone')}
            />

            <Select label="Herkunft" error={errors.origin?.message} {...register('origin')}>
              {origins.length === 0 && <option value="">Keine Herkunft verfügbar</option>}
              {origins.map((option) => (
                <option key={option.origin} value={option.origin}>
                  {option.originLabel}
                </option>
              ))}
            </Select>

            <Select
              className={form.full}
              label="Studio auswählen"
              error={errors.studio?.message}
              {...register('studio')}
            >
              {locations.map((studio) => (
                <option key={studio.publicId} value={studio.publicId}>
                  {studio.name}
                </option>
              ))}
            </Select>

            <Textarea
              className={form.full}
              label="Zusatzinfo"
              optional
              rows={3}
              error={errors.notes?.message}
              {...register('notes')}
            />
          </div>

          <label className={styles.consent}>
            <Checkbox
              className={styles.checkbox}
              invalid={Boolean(errors.consent)}
              {...register('consent')}
            />
            <span>
              Ich willige in die Datenverarbeitung gemäß der Datenschutzerklärung ein Datenschutz
              {errors.consent?.message && (
                <span className={styles.consentError}>{errors.consent.message}</span>
              )}
            </span>
          </label>
        </FormSection>
      </form>
    </Modal>
  )
}
